import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { AngularSplitModule } from 'angular-split';

import { toCanvas } from 'html-to-image';
import { MarkdownModule } from 'ngx-markdown';

import { AppSharedModule } from 'app/app-shared.module';
import { IExportCanvas } from '../../models/export-canvas';

@Component({
  selector: 'app-widget-markdown-editor',
  imports: [AppSharedModule, MarkdownModule, AngularSplitModule],
  templateUrl: './widget-markdown-editor.component.html',
  styleUrls: ['./widget-markdown-editor.component.scss'],
})
export class WidgetMarkdownEditorComponent implements IExportCanvas {
  @Input() content: string;
  @Input() mode: 'view' | 'edit';

  @Output() contentChange = new EventEmitter<string>();
  @Output() editFinished = new EventEmitter<void>();

  @ViewChild('markdownContainer', { read: ElementRef })
  markdownContainer: ElementRef<HTMLDivElement>;

  async exportCanvas(): Promise<HTMLCanvasElement> {
    const divElement = this.markdownContainer.nativeElement;

    const originalOverflow = divElement.style.overflow;
    const originalHeight = divElement.style.height;
    const clientHeight = divElement.clientHeight;
    const scrollHeight = divElement.scrollHeight;

    divElement.style.overflow = 'visible';
    divElement.style.height = scrollHeight > clientHeight ? 'auto' : `${scrollHeight}px`;

    const canvas = await toCanvas(divElement);

    divElement.style.overflow = originalOverflow;
    divElement.style.height = originalHeight;

    return canvas;
  }

  async exportCanvases(): Promise<HTMLCanvasElement[]> {
    const divElement = this.markdownContainer.nativeElement;
    const clientHeight = divElement.clientHeight;
    const scrollHeight = divElement.scrollHeight;

    const fullCanvas = await this.exportCanvas();
    if (clientHeight === 0 || scrollHeight <= clientHeight) {
      return [fullCanvas];
    }

    const spliceCount = Math.ceil(scrollHeight / clientHeight);
    const sliceHeight = Math.ceil(fullCanvas.height / spliceCount);
    const slices: HTMLCanvasElement[] = [];
    for (let i = 0; i < spliceCount; i++) {
      const srcY = i * sliceHeight;
      const actualHeight = Math.min(sliceHeight, fullCanvas.height - srcY);
      const slice = document.createElement('canvas');
      slice.width = fullCanvas.width;
      slice.height = actualHeight;

      const ctx = slice.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to create 2D canvas context for markdown export slice');
      }
      ctx.drawImage(
        fullCanvas,
        0,
        srcY,
        fullCanvas.width,
        actualHeight,
        0,
        0,
        fullCanvas.width,
        actualHeight
      );
      slices.push(slice);
    }
    return slices;
  }
}
